"use client";

import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { sequrityPinValidator } from "../../../utils/validators/validators";
import { Button } from "../index";
import "./SecureAccessModal.css";

interface SecureAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (pin: string) => void;
  title?: string;
  error?: string | null;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  timer?: number; // ⏳ مدت زمان باقیمانده بر حسب ثانیه
}

const SecureAccessModal: React.FC<SecureAccessModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title = "Enter Security PIN",
  error,
  confirmText = "Submit",
  cancelText = "Back",
  placeholder = "••••",
  timer,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [remainingTime, setRemainingTime] = useState(timer);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if (!timer) return;
    setRemainingTime(timer);

    const interval = setInterval(() => {
      setRemainingTime((prev) => (prev && prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const formik = useFormik({
    initialValues: { pin: "" },
    validationSchema: Yup.object({ pin: sequrityPinValidator }),
    onSubmit: ({ pin }) => onSubmit(pin),
  });

  if (!isOpen) return null;

  return createPortal(
    <div className="secure-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="secure-modal-container">
        {/* Header */}
        <div className="secure-modal-header">
          <h3 className="secure-modal-title">{title}</h3>
          <button className="secure-modal-close-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Timer */}
        {typeof remainingTime === "number" && (
          <div className="secure-timer-info">
            Access expires in <strong>{formatTime(remainingTime)}</strong>
          </div>
        )}

        {/* Body */}
        <div className="secure-modal-body">
          {error && <p className="secure-modal-error">{error}</p>}

          <form onSubmit={formik.handleSubmit} autoComplete="off">
            <div className="secure-pin-input-container">
              <input
                type={isVisible ? "text" : "password"}
                name="pin"
                className={`secure-pin-input ${formik.errors.pin ? "secure-pin-input-error" : ""}`}
                placeholder={placeholder}
                value={formik.values.pin}
                onChange={formik.handleChange}
                onFocus={(e) => (e.target.type = "password")}
                onBlur={(e) => (e.target.type = "text")}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setIsVisible(!isVisible)}
                className="secure-toggle-visibility"
              >
                {isVisible ? "Hide" : "Show"}
              </button>
            </div>
            {formik.errors.pin && <p className="secure-modal-error small">{formik.errors.pin}</p>}

            <div className="secure-modal-footer">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                {cancelText}
              </Button>
              <Button type="submit" size="sm" disabled={!formik.values.pin}>
                {confirmText}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SecureAccessModal;
